import { JSX } from "react";
import { useAuth } from "@clerk/react-router";
import { Navigate, Route, Routes } from "react-router-dom";
import { ErrorBoundary } from "./components/application/error-boundary/ErrorBoundary";
import { LoadingIndicator } from "./components/application/loading-indicator/loading-indicator";
import { RequireAcomAdmin } from "./components/application/require-acom-admin/RequireAcomAdmin";
import { RequireAdminPreview } from "./components/application/require-admin-preview/RequireAdminPreview";
import { RequireClientAdmin } from "./components/application/require-client-admin/RequireClientAdmin";
import { RequireNonAdmin } from "./components/application/require-non-admin/RequireNonAdmin";
import { SupportCrisp } from "./components/application/support-crisp/SupportCrisp";
import { useAcomAdmin } from "./hooks/use-acom-admin";
import { useClientAdmin } from "./hooks/use-client-admin";
import AppLayout from "./layouts/AppLayout";
import Home2Page from "./pages/Home2Page";
import HomePage from "./pages/HomePage";
import LegacyDocumentsPage from "./pages/LegacyDocumentsPage";
import LegacyFilesPage from "./pages/LegacyFilesPage";
import LegacyHomePage from "./pages/LegacyHomePage";
import LoginPage from "./pages/LoginPage";
import MockHomePage from "./pages/MockHomePage";
import PricingPage from "./pages/PricingPage";
// DISABLED: Sign-up page - uncomment to re-enable
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import SignUpPage from "./pages/SignUpPage";
import UploadStatusDemoPage from "./pages/UploadStatusDemoPage";
import AdminClientPage from "./pages/admin/AdminClientPage";
import AdminPage from "./pages/admin/AdminPage";
import ClientAdminPage from "./pages/client-admin/ClientAdminPage";
import { Theme } from "./providers/theme";
import "./styles/theme.css";
import "./styles/typography.css";
import { cx } from "./utils/cx";

// Font class - use Public Sans as the default body font
// Inter is used for headings (applied via CSS), JetBrains Mono for data (applied via utility classes)
const fontClass = "font-sans";

/**
 * Protected Route Wrapper using Clerk
 *
 * This is the recommended Clerk pattern for protecting routes:
 * - Wait for Clerk to load (isLoaded)
 * - Check if user is signed in (isSignedIn)
 * - Redirect to login if not authenticated
 */
const RequireAuth = ({ children }: { children: JSX.Element }) => {
    const { isLoaded, isSignedIn } = useAuth();

    // Don't render anything until Clerk is ready
    if (!isLoaded) {
        return (
            <div className="flex h-screen w-full items-center justify-center bg-primary">
                <LoadingIndicator size="lg" label="Loading..." />
            </div>
        );
    }

    // Redirect to login if not signed in
    if (!isSignedIn) {
        return <Navigate to="/login" replace />;
    }

    return children;
};

/**
 * Redirects root and unknown routes based on auth state and role:
 * - Signed out -> /login
 * - ACOM admin -> /admin
 * - ClientAdmin (org:admin in any non-ACOM org) -> /client-admin
 * - Everyone else -> /home
 */
function AuthRedirect() {
    const { isLoaded: authLoaded, isSignedIn } = useAuth();
    const { isAcomAdmin, isLoaded: adminLoaded } = useAcomAdmin();
    const { isClientAdmin, isLoaded: clientAdminLoaded } = useClientAdmin();

    if (!authLoaded || !adminLoaded || !clientAdminLoaded) {
        return (
            <div className="flex h-screen w-full items-center justify-center bg-primary">
                <LoadingIndicator size="lg" label="Loading..." />
            </div>
        );
    }
    if (!isSignedIn) {
        return <Navigate to="/login" replace />;
    }
    if (isAcomAdmin) {
        return <Navigate to="/admin" replace />;
    }
    if (isClientAdmin) {
        return <Navigate to="/client-admin" replace />;
    }
    return <Navigate to="/home" replace />;
}

function App() {
    return (
        <div className={cx(fontClass, "bg-primary antialiased")}>
            <SupportCrisp />
            <Theme>
                <ErrorBoundary>
                    <Routes>
                        {/* Public Routes: Login */}
                        <Route path="/login/*" element={<LoginPage />} />
                        {/* DISABLED: Sign-up route - uncomment to re-enable */}
                        {/* <Route path="/signup/*" element={<SignUpPage />} /> */}

                        {/* Redirect root based on auth state */}
                        <Route path="/" element={<AuthRedirect />} />

                        {/* Pricing page - public */}
                        <Route path="/pricing" element={<PricingPage />} />

                        {/* Home — main home (standalone, no AppLayout) */}
                        <Route
                            path="/home"
                            element={
                                <RequireAuth>
                                    <RequireNonAdmin>
                                        <HomePage />
                                    </RequireNonAdmin>
                                </RequireAuth>
                            }
                        />

                        {/* App routes (with layout) - legacy + admin + dev */}
                        <Route
                            element={
                                <RequireAuth>
                                    <AppLayout />
                                </RequireAuth>
                            }
                        >
                            <Route path="/legacy-home" element={<LegacyHomePage />} />
                            <Route path="/legacy-files" element={<LegacyFilesPage />} />
                            <Route path="/legacy-documents" element={<LegacyDocumentsPage />} />
                            <Route path="/home2" element={<Home2Page />} />
                            <Route path="/upload-status-demo" element={<UploadStatusDemoPage />} />
                            <Route path="/mock-home" element={<MockHomePage />} />
                        </Route>

                        {/* Admin routes — standalone (no AppLayout navbar) */}
                        <Route
                            path="/admin"
                            element={
                                <RequireAuth>
                                    <RequireAcomAdmin>
                                        <AdminPage />
                                    </RequireAcomAdmin>
                                </RequireAuth>
                            }
                        />
                        <Route
                            path="/admin/clients/:clientId"
                            element={
                                <RequireAuth>
                                    <RequireAcomAdmin>
                                        <AdminClientPage />
                                    </RequireAcomAdmin>
                                </RequireAuth>
                            }
                        />

                        {/* Admin preview — ACOM admin viewing a client's user UI */}
                        <Route
                            path="/admin/preview/:clientId/home"
                            element={
                                <RequireAuth>
                                    <RequireAdminPreview>
                                        <HomePage />
                                    </RequireAdminPreview>
                                </RequireAuth>
                            }
                        />

                        {/* ClientAdmin console — standalone (no AppLayout navbar) */}
                        <Route
                            path="/client-admin"
                            element={
                                <RequireAuth>
                                    <RequireClientAdmin>
                                        <ClientAdminPage />
                                    </RequireClientAdmin>
                                </RequireAuth>
                            }
                        />

                        {/* Unknown routes -> redirect based on auth */}
                        <Route path="*" element={<AuthRedirect />} />
                    </Routes>
                </ErrorBoundary>
            </Theme>
        </div>
    );
}

export default App;
